import { Box, Typography, Paper, alpha, useTheme } from "@mui/material";
import AccountBalanceWalletOutlined from "@mui/icons-material/AccountBalanceWalletOutlined";
import SouthWestRounded from "@mui/icons-material/SouthWestRounded";
import NorthEastRounded from "@mui/icons-material/NorthEastRounded";
import AcUnitRounded from "@mui/icons-material/AcUnitRounded";
import { SvgIconComponent } from "@mui/icons-material";
import { useMainStore } from "../store/store";
import { useBalance } from "../hooks/useBalance";
import { Loading, Error, PageHeader } from "../components";
import { symbols } from "../utils/currency";
import { TBalance } from "../utils/types";

/** One slice of the balance: label, amount and its share of the total. */
const BalanceTile: React.FC<{
  label: string;
  hint: string;
  value: number;
  total: number;
  /** Vivid hue for the meter and icon tile. */
  color: string;
  icon: SvgIconComponent;
  symbol: string;
}> = ({ label, hint, value, total, color, icon: Icon, symbol }) => {
  const share = total > 0 ? Math.round((value / total) * 100) : 0;

  return (
    <Paper sx={{ p: 2.5, borderRadius: "18px" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography
            color="text.secondary"
            sx={{ fontSize: 13, fontWeight: 620 }}
          >
            {label}
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: 11.5, mt: 0.25 }}>
            {hint}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "12px",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            color,
            bgcolor: alpha(color, 0.12),
          }}
        >
          <Icon fontSize="small" />
        </Box>
      </Box>

      <Typography
        sx={{
          fontSize: 26,
          fontWeight: 740,
          letterSpacing: "-0.03em",
          lineHeight: 1.1,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value.toLocaleString("en-US")}
        <Typography
          component="span"
          sx={{ fontSize: 15, fontWeight: 700, ml: 0.5 }}
          color="text.secondary"
        >
          {symbol}
        </Typography>
      </Typography>

      <Box
        sx={{
          mt: 1.5,
          height: 6,
          borderRadius: 999,
          overflow: "hidden",
          backgroundColor: alpha(color, 0.14),
        }}
      >
        <Box
          sx={{
            width: `${share}%`,
            height: "100%",
            borderRadius: 999,
            backgroundColor: color,
          }}
        />
      </Box>
      <Typography
        color="text.secondary"
        sx={{ fontSize: 11.5, mt: 0.75, fontWeight: 600 }}
      >
        {share}% of total
      </Typography>
    </Paper>
  );
};

export default function BalancePage() {
  const theme = useTheme();
  const { curr } = useMainStore();
  const { balance, error, loading } = useBalance();

  const currSymbol = symbols[curr] ?? curr;
  const isUSDT = curr.toUpperCase() === "USDT";
  const currKey = curr.toLowerCase();

  const getVal = (section: keyof TBalance) =>
    isUSDT
      ? (balance?.[section]?.usdt ?? 0)
      : (balance?.[section]?.fiat?.[currKey] ?? 0);

  const deposit = getVal("deposit");
  const payout = getVal("payout");
  const frozen = getVal("frozen");
  const total = deposit + payout + frozen;

  if (error) {
    return <Error error={error} />;
  }
  if (loading) {
    return <Loading variant="cards" />;
  }

  return (
    <Box
      sx={{
        width: "100%",
        paddingTop: "25px",
        maxHeight: {
          xs: "calc(var(--content-max-height) + 32.5dvh)",
          md: "82vh",
        },
        overflow: "auto",
      }}
    >
      <Box sx={{ width: "96%", margin: "0 auto", pb: 3 }}>
        <PageHeader
          title="Balance"
          description="Settlement position across your operations"
          icon={AccountBalanceWalletOutlined}
        />

        <Paper
          sx={{
            p: { xs: 3, sm: 4 },
            mb: 2.5,
            borderRadius: "22px",
            position: "relative",
            overflow: "hidden",
            color: "#fff",
            border: 0,
            background:
              "linear-gradient(140deg, #7770ff 0%, #5a52ea 52%, #3831b5 100%)",
            boxShadow: "0 22px 50px rgba(99, 91, 255, 0.30)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: 320,
              height: 320,
              borderRadius: "50%",
              right: -130,
              top: -160,
              border: "62px solid rgba(255,255,255,.08)",
              pointerEvents: "none",
            }}
          />

          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: ".06em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,.72)",
            }}
          >
            Total balance
          </Typography>
          <Typography
            sx={{
              mt: 1,
              fontSize: { xs: 38, sm: 48 },
              fontWeight: 780,
              letterSpacing: "-0.04em",
              lineHeight: 1.05,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {total.toLocaleString("en-US")}
            <Typography
              component="span"
              sx={{
                fontSize: { xs: 20, sm: 24 },
                fontWeight: 700,
                ml: 1,
                color: "rgba(255,255,255,.76)",
              }}
            >
              {currSymbol}
            </Typography>
          </Typography>
          <Typography
            sx={{ mt: 1.5, fontSize: 14, color: "rgba(255,255,255,.72)" }}
          >
            Shown in {curr}. Switch the currency in the header to see other
            settlement accounts.
          </Typography>
        </Paper>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 2,
          }}
        >
          <BalanceTile
            label="Deposit"
            hint="Collected from your customers"
            value={deposit}
            total={total}
            color={theme.palette.success.main}
            icon={SouthWestRounded}
            symbol={currSymbol}
          />
          <BalanceTile
            label="Payout"
            hint="Available amount for payout operations"
            value={payout}
            total={total}
            color={theme.palette.primary.main}
            icon={NorthEastRounded}
            symbol={currSymbol}
          />
          <BalanceTile
            label="Frozen"
            hint="Held for active payout operations"
            value={frozen}
            total={total}
            color={theme.palette.info.main}
            icon={AcUnitRounded}
            symbol={currSymbol}
          />
        </Box>
      </Box>
    </Box>
  );
}
